import { extractPolicyExpirationDate } from '@src/utility/cloudfront';
import { pickUnderlyingVideoElements } from '@src/utility/stackedLayout';
import { populateStoryQueryWithMissingFields } from '@src/utility/story';
import { makeAutoObservable } from 'mobx';

import { DEFAULT_PREVIEW_VIDEO_RES } from '../VideoCreatorStore';

import type { CurrentLoadedVideo } from '../VideoCreatorStore';
import type VideoCreatorStore from '../VideoCreatorStore';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { VideoVersion } from '@src/types.ts/story';

class RefreshManager {
  store: VideoCreatorStore;

  constructor(store: VideoCreatorStore) {
    makeAutoObservable(this, {
      store: false,
    });
    this.store = store;
  }

  public handleStoryPopulatedFields = async (signal?: AbortSignal) => {
    if (!this.hasOriginalVideoExpired()) {
      return;
    }
    console.debug('RefreshManager: Cloudfront urls got stale, refreshing');

    await this.refreshStoryFields(signal);

    // depends on story to be refreshed first
    await this.refreshVideoSource();
  };

  private hasOriginalVideoExpired(): boolean {
    const { story } = this.store;
    if (!story || !story.useAws) {
      return false;
    }

    const expirationDate = extractPolicyExpirationDate(story.originalVideo.url);
    if (!expirationDate) {
      return false;
    }

    const hasExpired = new Date() > expirationDate;
    return hasExpired;
  }

  private async refreshStoryFields(signal?: AbortSignal) {
    const { story } = this.store;
    if (!story) {
      return;
    }
    try {
      const refreshedMissingFields = await populateStoryQueryWithMissingFields(
        story,
        signal,
      );
      if (!refreshedMissingFields) {
        return;
      }
      story.originalVideo = refreshedMissingFields.originalVideo;
      story.selfHostedVideo = refreshedMissingFields.selfHostedVideo;
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('RefreshManager: Failed to refresh story fields:', error);
      }
    }
  }

  private async refreshVideoSource() {
    const { story, currentVideo } = this.store;
    if (
      !story ||
      !currentVideo ||
      !story.otherVideos?.some(
        v =>
          v.id === currentVideo.id ||
          v.associatedVideos.find(av => av.id === currentVideo.id),
      )
    ) {
      return;
    }
    const editor = this.store.rootStore.editorStore?.editor;
    if (!editor) {
      return;
    }
    const candidates = this.collectOriginalVideoElements(editor);
    if (candidates.length === 0) {
      return;
    }

    const refreshedUrl = this.store.getValidVideoSourceUrlForResLevel(
      DEFAULT_PREVIEW_VIDEO_RES,
    );
    if (!refreshedUrl) {
      console.warn(
        'RefreshManager: no valid source URL, leaving the expired ones in place',
        { candidates: candidates.length },
      );
      return;
    }

    // Skip the elements already on this URL. `refreshStoryFields` swallows its
    // errors, so a failed story refresh leaves the expired URL in place and
    // every element "updates" to the value it already has — and without this
    // the refresh cascade would then run on every tab focus, not once per
    // policy window.
    const updates = new Map<string, Record<string, any>>();
    for (const { id, source } of candidates) {
      if (source !== refreshedUrl) {
        updates.set(id, { source: refreshedUrl });
      }
    }
    if (updates.size === 0) {
      return;
    }

    await this.cacheNewVideoSourceUrl();

    // Re-sign in place. Round-tripping through loadVideoSource rebuilt the
    // timeline from element-only JSON, which carries neither the scenes nor the
    // transcript track — a URL swap wiped both (PLAT-10547).
    //
    // One command for all of them, not one each: a per-element command emits
    // ELEMENT_UPDATED plus two TIMELINE_CHANGED, and the EditorStore observer
    // answers every one with a full refreshTimeline/refreshTranscript/
    // refreshKaraoke pass — 3N of them for a story cut into N chunks.
    //
    // No undo entry: re-signing is not a user edit, and undoing it would
    // restore the expired URL.
    if (!editor.commandUpdateMultipleElements(updates, false)) {
      console.warn('RefreshManager: failed to re-sign video sources', {
        elements: updates.size,
      });
    }
    // No refreshTimeline call: the EditorStore observer answers the command's
    // TIMELINE_CHANGED with one (PROJECT_LOADED was unobserved, which is why
    // the old loadVideoSource path had to refresh here).
  }

  /**
   * The video elements playing the story's original video, with the source
   * they currently hold. Includes those nested in a composition (stacked
   * layout), which `allElements` does not flatten but `editor.getElementById`
   * still resolves.
   */
  private collectOriginalVideoElements(
    editor: IVideoEditor,
  ): { id: string; source: unknown }[] {
    const elements: { id: string; source: unknown }[] = [];
    for (const element of editor.timeline.allElements) {
      const elementJson = element.toJSON() as Record<string, unknown>;
      if (!this.store.isOriginalVideoElement(elementJson)) {
        continue;
      }
      for (const videoJson of pickUnderlyingVideoElements(elementJson)) {
        if (typeof videoJson.id === 'string') {
          elements.push({ id: videoJson.id, source: videoJson.source });
        }
      }
    }
    return elements;
  }

  private async cacheNewVideoSourceUrl() {
    const { story } = this.store;
    if (!story) {
      return;
    }
    const videoSourceUrl = story.originalVideo.video.mp4UrlLow;
    const renderer = this.store.renderer;
    if (!videoSourceUrl || !renderer) {
      return;
    }
    try {
      await renderer.warmAsset(videoSourceUrl);
    } catch (error) {
      console.error('RefreshManager: Error caching video source:', error);
    }
  }

  public handleCurrentVideoVersion = async (
    newVideoVersionHandler?: (
      currentVideo: CurrentLoadedVideo,
      newVersion: VideoVersion,
    ) => void,
  ) => {
    const { currentVideo } = this.store;
    if (!currentVideo?.id || !this.store.isCurrentVideoTheLatest) {
      console.debug(
        'RefreshManager: No current video or not the latest version',
        [currentVideo?.id, this.store.isCurrentVideoTheLatest],
      );
      return;
    }
    if (
      this.store.renderingVideoIds.includes(currentVideo.id) ||
      this.store.isVersionsHistoryLoading
    ) {
      // Version will be updated once rendering is done, no need to check now
      console.debug(
        'RefreshManager: Current video is still rendering, skipping version check',
        currentVideo.id,
      );
      return;
    }
    try {
      const { videoVersions } =
        await this.store.datoClientStore.videoRepository.getVersionsByVideoId(
          currentVideo.id,
          5, // pageSize
          0, // page
          this.store.rootStore.userIdentityStore.isExternalUser(),
        );
      const latestVersion = videoVersions.find(v => v.meta.is_current);
      if (
        latestVersion?.versionId &&
        latestVersion.versionId !== currentVideo.versionId
      ) {
        if (newVideoVersionHandler) {
          newVideoVersionHandler(currentVideo, latestVersion);
        } else {
          this.store.showToast({
            state: 'warning',
            message: 'There’s a newer version of the video available…',
          });
        }
      } else {
        console.debug('RefreshManager: No new video version found', [
          currentVideo?.versionId,
          latestVersion?.versionId,
        ]);
      }
    } catch (error) {
      console.error('RefreshManager: Error fetching video versions:', error);
    }
  };
}

export default RefreshManager;
